import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../api';
import {
  canDeleteRole,
  canModifyRole,
  canEditRoleBadge,
  canReorderRole,
  canTogglePermission,
  formatRoleLabel,
  getRoleRank,
  getValidReassignTargetsForDelete,
  isPermissionChecked,
  reorderRolesList,
  ROLE_DROP_END,
  sortRoles,
  VIEW_ADMIN_PANEL,
  HEAD_ADMIN_ROLE,
} from '../utils/roleManagement';
import BadgeUploader from './BadgeUploader';
import './RoleManagement.css';

function DragHandle({ onPointerDown }) {
  return (
    <button
      type="button"
      className="role-drag-handle"
      aria-label="Drag to reorder role"
      onPointerDown={onPointerDown}
    >
      <span className="role-drag-dots" aria-hidden="true">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <span key={i} />
        ))}
      </span>
    </button>
  );
}

function RoleCardHeader({
  role,
  isExpanded,
  permCount,
  canEdit,
  deleteCheck,
  onToggle,
  onCopy,
  onDelete,
}) {
  return (
    <div className="role-card-header">
      <button
        type="button"
        className="role-expand-btn"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-label={isExpanded ? 'Collapse role' : 'Expand role'}
      >
        <span className="role-expand-icon" aria-hidden="true">▸</span>
      </button>
      <button type="button" className="role-card-line" onClick={onToggle}>
        <span className="role-card-title">{formatRoleLabel(role.role_name)}</span>
        <span className="role-card-meta">
          {permCount} permission{permCount === 1 ? '' : 's'}
          {role.user_count > 0 ? ` · ${role.user_count} user${role.user_count === 1 ? '' : 's'}` : ''}
        </span>
      </button>
      {canEdit && (
        <div className="role-card-actions">
          <button type="button" className="role-action-btn" onClick={onCopy}>
            Copy
          </button>
          <button
            type="button"
            className="role-action-btn role-action-btn--danger"
            onClick={onDelete}
            disabled={!deleteCheck.ok}
            title={!deleteCheck.ok ? deleteCheck.message : undefined}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

function RoleManagement() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [meta, setMeta] = useState({
    actorRoleId: null,
    actorRoleName: '',
    actorRank: -1,
    actorPermissions: [],
    canManageRoles: false,
    isSiteOwner: false,
  });
  const [expanded, setExpanded] = useState({});
  const [newRoleName, setNewRoleName] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reassignRoleId, setReassignRoleId] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [dragRoleId, setDragRoleId] = useState(null);
  const [dragOverRoleId, setDragOverRoleId] = useState(null);
  const [dragPointer, setDragPointer] = useState(null);
  const [dragOffset, setDragOffset] = useState(null);
  const [dragSlotHeight, setDragSlotHeight] = useState(48);
  const rowRefs = useRef({});
  const bottomDropRef = useRef(null);

  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3500);
  };

  const loadData = useCallback(async () => {
    const res = await apiFetch('/admin/roles/management');
    if (!res.ok) throw new Error('Failed to load roles');
    const data = await res.json();
    setRoles(sortRoles(data.roles || []));
    setPermissions(data.permissions || []);
    setMeta(data.meta || {});
    return data;
  }, []);

  useEffect(() => {
    loadData().catch((err) => setError(err.message));
  }, [loadData]);

  const toggleExpanded = (roleId) => {
    setExpanded((prev) => ({ ...prev, [roleId]: !prev[roleId] }));
  };

  const updateRoleInList = (updatedRole) => {
    setRoles((prev) => sortRoles(prev.map((r) => (
      Number(r.role_id) === Number(updatedRole.role_id) ? { ...r, ...updatedRole } : r
    ))));
  };

  const handleAddRole = async () => {
    const name = newRoleName.trim();
    if (!name) {
      setError('Enter a role name');
      return;
    }
    setError(null);
    try {
      const res = await apiFetch('/admin/roles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role_name: name }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to add role');
      setRoles((prev) => sortRoles([...prev, { ...data, permissions: data.permissions || [], user_count: 0 }]));
      setNewRoleName('');
      flash('Role added at bottom of list');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCopyRole = async (role) => {
    setError(null);
    try {
      const res = await apiFetch(`/admin/roles/${role.role_id}/copy`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to copy role');
      setRoles((prev) => sortRoles([...prev, data]));
      flash(`Copied ${formatRoleLabel(role.role_name)}`);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDefaultSignupChange = async (role, currentlyChecked) => {
    setError(null);
    try {
      const res = await apiFetch(`/admin/roles/${role.role_id}/default-signup`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !currentlyChecked }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to update default signup role');
      if (data.roles) setRoles(sortRoles(data.roles));
      else await loadData();
      flash(
        !currentlyChecked
          ? `New signups will join ${formatRoleLabel(role.role_name)}`
          : 'Default signup role cleared — new users get the lowest role',
      );
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePermissionChange = async (role, permission, currentlyChecked) => {
    const actorRole = meta.actorPermissions.includes('all') ? HEAD_ADMIN_ROLE : meta.actorRoleName;
    if (!canTogglePermission(
      roles,
      meta.actorRoleId,
      role,
      permission.permission_name,
      meta.actorPermissions,
      actorRole,
      meta.canManageRoles,
    )) return;

    setError(null);
    try {
      const res = await apiFetch(`/admin/roles/${role.role_id}/permissions`, {
        method: currentlyChecked ? 'DELETE' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permission_id: permission.permission_id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to update permission');
      if (data.role) updateRoleInList(data.role);
      else await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const userRoleForChecks = meta.actorPermissions.includes('all') ? HEAD_ADMIN_ROLE : meta.actorRoleName;

  const getReassignTargets = (role) => getValidReassignTargetsForDelete(
    roles,
    meta.actorRoleId,
    role,
    userRoleForChecks,
    meta.canManageRoles,
  );

  const openDeleteModal = (role) => {
    const targets = getReassignTargets(role);
    setDeleteTarget(role);
    setReassignRoleId(targets[0] ? String(targets[0].role_id) : '');
  };

  const handleDeleteRole = async () => {
    if (!deleteTarget || !reassignRoleId) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await apiFetch(`/admin/roles/${deleteTarget.role_id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reassignToRoleId: Number(reassignRoleId) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to delete role');
      setRoles(sortRoles(data.roles || []));
      setDeleteTarget(null);
      flash(data.usersReassigned
        ? `Role deleted; ${data.usersReassigned} user(s) reassigned`
        : 'Role deleted');
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const persistReorder = async (nextRoles) => {
    const roleIds = nextRoles.map((r) => r.role_id);
    const res = await apiFetch('/admin/roles/reorder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roleIds }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || 'Failed to reorder roles');
    setRoles(sortRoles(data.roles || nextRoles));
  };

  const getReorderableRoles = useCallback(() => {
    const sorted = sortRoles(roles);
    const actorRank = getRoleRank(roles, meta.actorRoleId);
    return sorted.filter((role) => {
      const rank = getRoleRank(roles, role.role_id);
      return rank > actorRank && role.role_name !== HEAD_ADMIN_ROLE;
    });
  }, [roles, meta.actorRoleId]);

  const findDropTarget = useCallback((clientY) => {
    const bottomEl = bottomDropRef.current;
    if (bottomEl) {
      const bottomRect = bottomEl.getBoundingClientRect();
      if (clientY >= bottomRect.top + bottomRect.height * 0.35) {
        return ROLE_DROP_END;
      }
    }

    const reorderable = getReorderableRoles();
    for (const role of reorderable) {
      const el = rowRefs.current[role.role_id];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      const mid = rect.top + rect.height / 2;
      if (clientY < mid) return role.role_id;
    }
    return ROLE_DROP_END;
  }, [getReorderableRoles]);

  const startDrag = (roleId, e) => {
    const shell = rowRefs.current[roleId];
    const rect = shell?.getBoundingClientRect();
    if (!rect) return;
    setDragSlotHeight(rect.height);
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      width: rect.width,
    });
    setDragPointer({ x: e.clientX, y: e.clientY });
    setDragRoleId(roleId);
    setDragOverRoleId(roleId);
  };

  const previewRoles = dragRoleId && dragOverRoleId
    ? reorderRolesList(roles, dragRoleId, dragOverRoleId)
    : sortRoles(roles);

  const draggedRole = dragRoleId
    ? roles.find((r) => Number(r.role_id) === Number(dragRoleId))
    : null;

  useEffect(() => {
    if (!dragRoleId) return undefined;

    const onMove = (e) => {
      setDragPointer({ x: e.clientX, y: e.clientY });
      const over = findDropTarget(e.clientY);
      if (over) setDragOverRoleId(over);
    };

    const onUp = async () => {
      const overId = dragOverRoleId;
      const fromId = dragRoleId;
      setDragRoleId(null);
      setDragOverRoleId(null);
      setDragPointer(null);
      setDragOffset(null);
      if (!overId || !fromId || Number(overId) === Number(fromId)) return;
      const next = reorderRolesList(roles, fromId, overId);
      setRoles(next);
      try {
        await persistReorder(next);
      } catch (err) {
        setError(err.message);
        loadData();
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [dragRoleId, dragOverRoleId, findDropTarget, roles, loadData]);

  return (
    <div className="role-management-section">
      <h2>Role Management</h2>
      <p className="role-management-hint">
        Roles are ordered top to bottom (highest authority first). You can only change roles and
        permissions below your own level.
      </p>
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {meta.isSiteOwner && (
        <p className="role-signup-default-global-hint">
          As site owner you can mark one role below as the default for new signups.
          If none is selected, new users receive the lowest role in the list.
        </p>
      )}

      {meta.canManageRoles && (
        <div className="add-role-section">
          <h3>Add role</h3>
          <div className="add-role-row">
            <input
              type="text"
              placeholder="New role name"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddRole()}
            />
            <button type="button" onClick={handleAddRole}>Add to bottom</button>
          </div>
        </div>
      )}

      <div className="roles-list">
        <h3>Roles</h3>
        <ul className={['role-cards', dragRoleId ? 'role-cards--dragging' : ''].filter(Boolean).join(' ')}>
          {previewRoles.map((role) => {
            const isExpanded = !!expanded[role.role_id];
            const isHead = role.role_name === HEAD_ADMIN_ROLE;
            const isDragging = Number(dragRoleId) === Number(role.role_id);
            const canEdit = canModifyRole(
              roles,
              meta.actorRoleId,
              role,
              userRoleForChecks,
              meta.canManageRoles,
            );
            const canDrag = canReorderRole(
              roles,
              meta.actorRoleId,
              role,
              userRoleForChecks,
              meta.canManageRoles,
            );
            const deleteCheck = canDeleteRole(
              roles,
              meta.actorRoleId,
              role,
              role.user_count,
              userRoleForChecks,
              meta.canManageRoles,
            );
            const canEditBadge = canEditRoleBadge(
              roles,
              meta.actorRoleId,
              role,
              userRoleForChecks,
              meta.canManageRoles,
            );
            const permCount = isHead ? permissions.length : (role.permissions || []).length;

            if (isDragging) {
              return (
                <li
                  key={role.role_id}
                  ref={(el) => { rowRefs.current[role.role_id] = el; }}
                  className="role-card role-card--slot"
                  aria-hidden="true"
                >
                  <div
                    className="role-drag-slot"
                    style={{ minHeight: dragSlotHeight }}
                  />
                </li>
              );
            }

            return (
              <li
                key={role.role_id}
                ref={(el) => { rowRefs.current[role.role_id] = el; }}
                className={[
                  'role-card',
                  isExpanded ? 'role-card--expanded' : '',
                  canDrag ? 'role-card--has-handle' : '',
                ].filter(Boolean).join(' ')}
              >
                {canDrag && (
                  <div className="role-card-chrome">
                    <DragHandle
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        startDrag(role.role_id, e);
                      }}
                    />
                  </div>
                )}

                <RoleCardHeader
                  role={role}
                  isExpanded={isExpanded}
                  permCount={permCount}
                  canEdit={canEdit}
                  deleteCheck={deleteCheck}
                  onToggle={() => toggleExpanded(role.role_id)}
                  onCopy={() => handleCopyRole(role)}
                  onDelete={() => openDeleteModal(role)}
                />

                {isExpanded && (
                  <div className="role-permissions-panel">
                    {meta.isSiteOwner && !isHead && role.role_name !== 'banned' && (
                      <div className="role-signup-default">
                        <label className="role-signup-default-item">
                          <input
                            type="checkbox"
                            checked={!!role.is_default_signup_role}
                            onChange={() => handleDefaultSignupChange(role, !!role.is_default_signup_role)}
                          />
                          <span>New users join this role</span>
                        </label>
                      </div>
                    )}
                    {canEditBadge && (
                      <BadgeUploader
                        kind="role"
                        entityId={role.role_id}
                        entity={role}
                        canEdit
                        label={isHead
                          ? 'Site owner chat badge (shown before club badge)'
                          : 'Role chat badge (shown before club badge)'}
                        onUpdated={(updated) => updateRoleInList({ ...role, ...updated })}
                        onError={setError}
                      />
                    )}
                    {isHead ? (
                      <p className="role-permissions-note">
                        Site owner has all permissions (hardcoded). You can still set a chat badge above.
                      </p>
                    ) : (
                      <div className="role-permissions-grid">
                        {permissions.map((permission) => {
                          const checked = isPermissionChecked(role, permission.permission_name);
                          const canToggle = canTogglePermission(
                            roles,
                            meta.actorRoleId,
                            role,
                            permission.permission_name,
                            meta.actorPermissions,
                            userRoleForChecks,
                            meta.canManageRoles,
                          );
                          const forced = permission.permission_name === VIEW_ADMIN_PANEL
                            && checked
                            && !canToggle
                            && canEdit;

                          return (
                            <label
                              key={permission.permission_id}
                              className={[
                                'role-permission-item',
                                !canToggle ? 'role-permission-item--locked' : '',
                                forced ? 'role-permission-item--forced' : '',
                              ].filter(Boolean).join(' ')}
                              title={permission.description || permission.permission_name}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={!canToggle}
                                onChange={() => handlePermissionChange(role, permission, checked)}
                              />
                              <span>{permission.permission_name}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
          {dragRoleId && (
            <li
              ref={bottomDropRef}
              className={[
                'role-drop-end',
                dragOverRoleId === ROLE_DROP_END ? 'role-drop-end--active' : '',
              ].filter(Boolean).join(' ')}
              aria-hidden="true"
            >
              <span className="role-drop-end-label">Drop here for lowest rank</span>
            </li>
          )}
        </ul>
      </div>

      {dragRoleId && dragPointer && dragOffset && draggedRole && (
        <div
          className="role-drag-ghost"
          style={{
            left: dragPointer.x - dragOffset.x,
            top: dragPointer.y - dragOffset.y,
            width: dragOffset.width,
          }}
        >
          <RoleCardHeader
            role={draggedRole}
            isExpanded={false}
            permCount={draggedRole.role_name === HEAD_ADMIN_ROLE
              ? permissions.length
              : (draggedRole.permissions || []).length}
            canEdit={false}
            deleteCheck={{ ok: true }}
            onToggle={() => {}}
            onCopy={() => {}}
            onDelete={() => {}}
          />
        </div>
      )}

      {deleteTarget && (() => {
        const reassignTargets = getReassignTargets(deleteTarget);
        const deleteBlocked = deleteTarget.user_count > 0 && reassignTargets.length === 0;
        return (
        <div className="role-delete-overlay" onClick={() => !deleting && setDeleteTarget(null)} role="presentation">
          <div className="role-delete-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3>Delete role?</h3>
            <p>
              Delete <strong>{formatRoleLabel(deleteTarget.role_name)}</strong>?
              {deleteTarget.user_count > 0 && (
                <>
                  {' '}
                  {deleteTarget.user_count} user{deleteTarget.user_count === 1 ? '' : 's'} will be
                  reassigned.
                </>
              )}
            </p>
            {deleteBlocked ? (
              <p className="role-delete-blocked">
                Cannot delete: users would have nowhere valid to reassign (no roles below yours
                except this one).
              </p>
            ) : (
            <label className="role-delete-reassign">
              Reassign users to
              <select
                value={reassignRoleId}
                onChange={(e) => setReassignRoleId(e.target.value)}
                disabled={deleting}
              >
                {reassignTargets.map((r) => (
                    <option key={r.role_id} value={r.role_id}>
                      {formatRoleLabel(r.role_name)}
                    </option>
                  ))}
              </select>
            </label>
            )}
            <div className="role-delete-actions">
              <button
                type="button"
                className="role-delete-confirm"
                onClick={handleDeleteRole}
                disabled={deleting || deleteBlocked || !reassignRoleId}
              >
                {deleting ? 'Deleting…' : 'Delete role'}
              </button>
              <button
                type="button"
                className="role-delete-cancel"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

export default RoleManagement;